import React, { useState, useMemo, useRef } from 'react';
import { confirmDialog } from '../components/ui/ImpulzaDialog';
import { reportHandledError } from '../utils/sentry';
import { supabase } from '../supabase';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-hot-toast';
import {
  ArrowLeft, Search, CheckSquare, Square,
  Calculator, History, FileText, Trash2,
  ChevronDown, ChevronUp, RefreshCcw,
  TrendingUp, Check, Calendar, DollarSign, PieChart, Layers
} from 'lucide-react';

const RENTABILIDAD_COSTS_UPDATED_EVENT = 'impulza:product-costs-updated';
const RENTABILIDAD_COSTS_UPDATED_STORAGE_KEY = 'impulza:product-costs-updated:v1';

// FIX CRÍTICO #1: helper de idempotencia para evitar doble aplicación de prorrateo.
const generateIdempotencyKey = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return 'idem-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 10);
};

// FIX CRÍTICO #1 (sanitización): el concepto se persiste embebido en `observaciones`
// con marcadores `[Prorrateo: <concepto> | $<monto> #<id>]`. Si el usuario ingresa
// `|`, `[`, `]` o saltos de línea, el parser de extracción se rompe y al "eliminar"
// ese gasto no se descuenta del costo. Sanitizamos antes de persistir.
const sanitizeConcepto = (value) =>
  String(value || '')
    .replace(/[|[\]\r\n]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 80);

// Marcador con ID único para identificar de manera unívoca cada prorrateo aplicado,
// y poder eliminarlo sin colisiones cuando hay varios con el mismo concepto.
const buildProrrateoMarker = ({ conceptoSanitized, impactoUnitario, idempotencyKey }) =>
  `[Prorrateo: ${conceptoSanitized} | $${impactoUnitario.toFixed(2)} #${idempotencyKey}]`;

/**
 * Módulo de Distribución de Costos - Estándar Enterprise Optimizado
 * Refactorizado estilo Productos.jsx
 * Ajuste: Icono de calendario en negro y clic integral en celda mantenidos.
 */
function ProduccionDistribucion({ onBack }) {
  const { profile } = useAuth();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [view, setView] = useState('distribuir');

  const [fechaDesde, setFechaDesde] = useState('');
  const [fechaHasta, setFechaHasta] = useState('');
  const [producciones, setProducciones] = useState([]);
  const [seleccionados, setSeleccionados] = useState([]);

  const [montoTotal, setMontoTotal] = useState('');
  const [concepto, setConcepto] = useState('');
  const [expandedId, setExpandedId] = useState(null);

  // FIX CRÍTICO #1: idempotency_key estable para esta operación de prorrateo.
  // Se regenera al limpiar el formulario (después de aplicar exitosamente).
  const idempotencyKeyRef = useRef(generateIdempotencyKey());

  const formatearFechaLocal = (fechaStr) => {
    if (!fechaStr) return "-";
    const [year, month, day] = fechaStr.split('T')[0].split('-');
    return `${day}/${month}/${year}`;
  };

  // FIX UX #32: KPI diferenciado.
  // Antes mostrábamos un único "Inversión Lotes" sumando costo_total que YA incluye
  // los prorrateos aplicados — confunde al usuario al alternar entre vistas porque
  // el mismo período "vale distinto" según los prorrateos cargados.
  // Ahora separamos:
  //   - inversionDirecta: solo costo de insumos (sin prorrateos sumados).
  //   - inversionTotalProduccion: costo total con prorrateos.
  const inversionTotalProduccion = useMemo(() => {
    return producciones.reduce((acc, p) => acc + Number(p.costo_total || 0), 0);
  }, [producciones]);

  const totalProrrateosAplicados = useMemo(() => {
    return producciones.reduce((acc, p) => {
      const matches = String(p.observaciones || '').match(/\$\s*([\d.,]+)/g) || [];
      const cantidad = Number(p.cantidad || 0);
      const sumaUnit = matches.reduce((a, m) => {
        const num = Number(String(m).replace(/[$\s]/g, '').replace(',', '.')) || 0;
        return a + num;
      }, 0);
      return acc + sumaUnit * cantidad;
    }, 0);
  }, [producciones]);

  const inversionDirecta = inversionTotalProduccion - totalProrrateosAplicados;

  const buscarProducciones = async () => {
    if (!fechaDesde || !fechaHasta) return toast.error("Seleccione rango de fechas");
    // FIX CRÍTICO #3 (defensa en profundidad): exigir organization_id.
    // Antes solo se confiaba en RLS; con un misconfig se podían cruzar datos entre orgs.
    if (!profile?.organization_id) return toast.error("Sesión no válida");

    setLoading(true);
    try {
      const dateHasta = new Date(fechaHasta);
      dateHasta.setDate(dateHasta.getDate() + 1);
      const diaSiguiente = dateHasta.toISOString().split('T')[0];

      let query = supabase
        .from('produccion')
        .select('*')
        .eq('organization_id', profile.organization_id)
        .eq('activo', true)
        .gte('fecha', fechaDesde)
        .lt('fecha', diaSiguiente);

      if (view === 'historial') {
        query = query.like('observaciones', '%[Prorrateo:%');
      }

      const { data, error } = await query.order('fecha', { ascending: false });
      if (error) throw error;
      setProducciones(data || []);
      if (view === 'distribuir') setSeleccionados(data?.map(p => p.id) || []);

    } catch (e) {
      reportHandledError(e, { feature: 'produccion:distribucion' });
      toast.error("Error al sincronizar datos");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateFecha = async (id, nuevaFecha) => {
    if (!nuevaFecha) return;
    const updateToast = toast.loading("Actualizando registro...");
    try {
      const { error } = await supabase
        .from('produccion')
        .update({ fecha: nuevaFecha })
        .eq('id', id);
      
      if (error) throw error;
      toast.success("Fecha actualizada", { id: updateToast });
      buscarProducciones(); 
    } catch (e) {
      toast.error("Error al actualizar fecha", { id: updateToast });
    }
  };

  const impactoUnitario = useMemo(() => {
    const monto = parseFloat(montoTotal) || 0;
    if (monto <= 0 || seleccionados.length === 0) return 0;
    const totalUnidades = producciones
      .filter(p => seleccionados.includes(p.id))
      .reduce((acc, p) => acc + Number(p.cantidad), 0);
    return totalUnidades > 0 ? (monto / totalUnidades) : 0;
  }, [montoTotal, seleccionados, producciones]);

  const totalUnidadesSeleccionadas = useMemo(() => {
    return producciones
      .filter(p => seleccionados.includes(p.id))
      .reduce((acc, p) => acc + Number(p.cantidad), 0);
  }, [seleccionados, producciones]);

  const toggleSeleccion = (id) => {
    if (seleccionados.includes(id)) {
      setSeleccionados(seleccionados.filter(sid => sid !== id));
    } else {
      setSeleccionados([...seleccionados, id]);
    }
  };

  const notifyRentabilidadRefresh = (productIds = []) => {
    if (typeof window === 'undefined') return;

    const payload = {
      productIds: Array.from(new Set((productIds || []).filter(Boolean))),
      updatedAt: new Date().toISOString(),
    };

    try {
      window.localStorage.setItem(RENTABILIDAD_COSTS_UPDATED_STORAGE_KEY, JSON.stringify(payload));
    } catch {
      // ignore storage errors
    }

    window.dispatchEvent(new CustomEvent(RENTABILIDAD_COSTS_UPDATED_EVENT, { detail: payload }));
  };

  const applyCostDeltaToProducts = async (productIds, deltaUnitario) => {
    const safeProductIds = Array.from(new Set((productIds || []).filter(Boolean)));
    const delta = Number(deltaUnitario || 0);

    if (!safeProductIds.length || !Number.isFinite(delta) || delta === 0) {
      return [];
    }

    const [productsRes, variantsRes] = await Promise.all([
      supabase.from('products').select('id, cost_price').in('id', safeProductIds),
      supabase.from('product_variants').select('id, product_id, cost_price').in('product_id', safeProductIds),
    ]);

    if (productsRes.error) throw productsRes.error;
    if (variantsRes.error) throw variantsRes.error;

    const productUpdates = (productsRes.data || []).map((product) =>
      supabase
        .from('products')
        .update({ cost_price: Number(Math.max(0, Number(product.cost_price || 0) + delta).toFixed(2)) })
        .eq('id', product.id)
    );

    const variantUpdates = (variantsRes.data || []).map((variant) =>
      supabase
        .from('product_variants')
        .update({ cost_price: Number(Math.max(0, Number(variant.cost_price || 0) + delta).toFixed(2)) })
        .eq('id', variant.id)
    );

    const results = await Promise.all([...productUpdates, ...variantUpdates]);
    const failed = results.find((result) => result?.error);
    if (failed?.error) throw failed.error;

    return safeProductIds;
  };

  // Parser robusto: usa regex sobre el formato sanitizado `<concepto> | $<monto> #<id>`
  const extractImpactFromDistribution = (gastoStr) => {
    if (!gastoStr) return 0;
    // Buscamos `$<monto>` evitando depender exclusivamente del split por `|` (que puede fallar
    // si datos legacy tienen caracteres inesperados).
    const match = String(gastoStr).match(/\$\s*([\d.,]+)/);
    if (!match) return 0;
    const rawAmount = match[1].replace(/\s+/g, '').replace(',', '.');
    const parsed = Number.parseFloat(rawAmount);
    return Number.isFinite(parsed) ? parsed : 0;
  };

  // Extrae el #idempotencyKey si existe en la marca (formato nuevo)
  const extractIdempotencyKey = (gastoStr) => {
    if (!gastoStr) return null;
    const match = String(gastoStr).match(/#([a-zA-Z0-9-]+)/);
    return match ? match[1] : null;
  };

  const handleAplicarCostos = async (e) => {
    e.preventDefault();
    if (impactoUnitario <= 0) return toast.error("Cargue monto y seleccione órdenes");
    if (!profile?.organization_id) return toast.error("Sesión no válida");

    const conceptoSanitized = sanitizeConcepto(concepto);
    if (!conceptoSanitized) {
      return toast.error("El concepto no puede estar vacío ni contener caracteres especiales (| [ ])");
    }

    // FIX CRÍTICO #1: confirmación previa, evita el doble click accidental
    if (!(await confirmDialog({
      title: 'Aplicar prorrateo',
      message: `Concepto: ${conceptoSanitized}`,
      details: [
        { label: 'Monto total', value: `$${(parseFloat(montoTotal) || 0).toLocaleString('es-AR')}` },
        { label: 'Impacto por unidad', value: `$${impactoUnitario.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` },
        { label: 'Órdenes', value: seleccionados.length },
      ],
      confirmLabel: 'Prorratear',
    }))) {
      return;
    }

    setSaving(true);
    const idempotencyKey = idempotencyKeyRef.current;

    try {
      const seleccionadasData = producciones.filter(p => seleccionados.includes(p.id));
      const productIdsSet = new Set();
      seleccionadasData.forEach((o) => { if (o.producto_id) productIdsSet.add(o.producto_id); });

      // ============================================================
      // FIX SERVER-SIDE: intentamos usar el RPC v2 `apply_production_prorrateo`.
      // Si la migración 20260516_production_rpcs_v2.sql aún no fue aplicada,
      // caemos al flujo legacy de UPDATE directo + marcas en observaciones.
      // ============================================================
      const rpcRes = await supabase.rpc('apply_production_prorrateo', {
        p_payload: {
          organization_id: profile.organization_id,
          concepto: conceptoSanitized,
          idempotency_key: idempotencyKey,
          produccion_ids: seleccionadasData.map((o) => o.id),
          monto_total: parseFloat(montoTotal) || 0,
        },
      });

      // PGRST202 = function does not exist (RPC v2 no aplicado todavía)
      const rpcNoDisponible = rpcRes.error && (
        rpcRes.error.code === 'PGRST202' ||
        /apply_production_prorrateo/i.test(rpcRes.error.message || '') ||
        /does not exist/i.test(rpcRes.error.message || '')
      );

      if (rpcRes.error && !rpcNoDisponible) {
        throw rpcRes.error;
      }

      if (!rpcRes.error) {
        // ✅ Camino RPC v2: el server ya hizo todo. Notificamos refresh y salimos.
        const data = rpcRes.data || {};
        if (data.omitidos_por_idempotencia > 0) {
          toast(`${data.omitidos_por_idempotencia} ya tenía este prorrateo aplicado (idempotency).`, { icon: 'ℹ️' });
        }
        const productosAfectados = Array.isArray(data.product_ids_afectados) ? data.product_ids_afectados : [];
        notifyRentabilidadRefresh(productosAfectados);

        setIsSuccess(true);
        toast.success(`Prorrateo aplicado a ${data.aplicados || 0} orden(es).`);

        setTimeout(() => {
          setMontoTotal('');
          setConcepto('');
          setIsSuccess(false);
          setSaving(false);
          idempotencyKeyRef.current = generateIdempotencyKey();
          buscarProducciones();
        }, 2000);
        return;
      }

      // ============================================================
      // FALLBACK LEGACY (RPC v2 no disponible): mantenemos el flujo viejo
      // con UPDATE directo + marcas en observaciones para no bloquear al usuario.
      // ============================================================
      console.warn('[ProduccionDistribucion] RPC apply_production_prorrateo no disponible; usando fallback legacy.');

      // Defensa cliente-side: si alguna orden ya tiene este idempotencyKey, abortamos
      const yaAplicada = seleccionadasData.find(
        (o) => String(o.observaciones || '').includes(`#${idempotencyKey}`),
      );
      if (yaAplicada) {
        toast.error('Este prorrateo ya fue aplicado a una de las órdenes seleccionadas.');
        idempotencyKeyRef.current = generateIdempotencyKey();
        return;
      }

      const updates = seleccionadasData.map((orden) => {
        const nuevoCostoUnitario = Number(orden.costo_unitario || 0) + impactoUnitario;
        const nuevoCostoTotal = Number(orden.costo_total || 0) + (impactoUnitario * Number(orden.cantidad || 0));
        const nuevaMarca = buildProrrateoMarker({
          conceptoSanitized,
          impactoUnitario,
          idempotencyKey,
        });

        return supabase
          .from('produccion')
          .update({
            costo_unitario: Number(nuevoCostoUnitario.toFixed(2)),
            costo_total: Number(nuevoCostoTotal.toFixed(2)),
            observaciones: `${orden.observaciones || ''} ${nuevaMarca}`.trim(),
          })
          .eq('id', orden.id)
          .eq('organization_id', profile.organization_id);
      });

      const results = await Promise.all(updates);
      const failed = results.find((r) => r?.error);
      if (failed?.error) throw failed.error;

      const updatedProductIds = await applyCostDeltaToProducts(
        Array.from(productIdsSet),
        impactoUnitario,
      );
      notifyRentabilidadRefresh(updatedProductIds);

      setIsSuccess(true);
      toast.success("Costos aplicados (modo legacy). Aplicá la migración RPC v2 para mejor trazabilidad.");

      setTimeout(() => {
        setMontoTotal('');
        setConcepto('');
        setIsSuccess(false);
        setSaving(false);
        idempotencyKeyRef.current = generateIdempotencyKey();
        buscarProducciones();
      }, 2000);

    } catch (e) {
      console.error('[ProduccionDistribucion] Error aplicando prorrateo:', e);
      toast.error("Error técnico al aplicar el prorrateo: " + (e?.message || ''));
      setSaving(false);
    }
  };

  const handleRemoveDistribution = async (orden, gastoStr) => {
    if (!(await confirmDialog({
      title: '¿Eliminar este gasto?',
      message: 'Se quita este gasto prorrateado de la orden y se recalcula su costo.',
      tone: 'danger',
      confirmLabel: 'Eliminar',
    }))) return;
    if (!profile?.organization_id) return toast.error("Sesión no válida");

    try {
      const impacto = extractImpactFromDistribution(gastoStr);
      const idempotencyKey = extractIdempotencyKey(gastoStr);

      if (impacto <= 0) {
        toast.error("No se pudo determinar el monto del prorrateo (formato legacy o corrupto).");
        return;
      }

      // ============================================================
      // FIX SERVER-SIDE: intentar usar reverse_production_prorrateo si el
      // prorrateo está en la tabla nueva `produccion_prorrateos` y tenemos su id.
      // Buscamos el prorrateo por (organization_id, idempotency_key, produccion_id).
      // Si no existe ahí, caemos al flujo legacy de UPDATE directo a observaciones.
      // ============================================================
      if (idempotencyKey) {
        const { data: prorrateoRow } = await supabase
          .from('produccion_prorrateos')
          .select('id, revertido_en')
          .eq('organization_id', profile.organization_id)
          .eq('idempotency_key', idempotencyKey)
          .eq('produccion_id', orden.id)
          .maybeSingle();

        if (prorrateoRow && !prorrateoRow.revertido_en) {
          const rpcRes = await supabase.rpc('reverse_production_prorrateo', {
            p_prorrateo_id: prorrateoRow.id,
            p_reason: 'Reversa manual desde UI',
          });
          if (!rpcRes.error) {
            notifyRentabilidadRefresh([orden.producto_id].filter(Boolean));
            toast.success("Prorrateo revertido");
            buscarProducciones();
            return;
          }
          console.warn('[ProduccionDistribucion] reverse_production_prorrateo falló, intentando fallback:', rpcRes.error);
        }
      }

      // ============================================================
      // FALLBACK LEGACY: marca embebida en observaciones (formato viejo)
      // ============================================================
      let nuevaObs;
      if (idempotencyKey) {
        const safeKey = idempotencyKey.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const regex = new RegExp(`\\[Prorrateo:[^\\]]*#${safeKey}\\]`, 'g');
        nuevaObs = String(orden.observaciones || '').replace(regex, '').replace(/\s{2,}/g, ' ').trim();
      } else {
        const marcaAEliminar = `[Prorrateo: ${gastoStr}]`;
        nuevaObs = String(orden.observaciones || '').replace(marcaAEliminar, '').replace(/\s{2,}/g, ' ').trim();
      }

      const nuevoCostoUnitario = Math.max(0, Number(orden.costo_unitario || 0) - impacto);
      const nuevoCostoTotal = Math.max(0, Number(orden.costo_total || 0) - (impacto * Number(orden.cantidad || 0)));

      const { error } = await supabase
        .from('produccion')
        .update({
          observaciones: nuevaObs,
          costo_unitario: Number(nuevoCostoUnitario.toFixed(2)),
          costo_total: Number(nuevoCostoTotal.toFixed(2)),
        })
        .eq('id', orden.id)
        .eq('organization_id', profile.organization_id);

      if (error) throw error;
      const updatedProductIds = await applyCostDeltaToProducts([orden.producto_id], -impacto);
      notifyRentabilidadRefresh(updatedProductIds);
      toast.success("Gasto eliminado y rentabilidad sincronizada");
      buscarProducciones();
    } catch (err) {
      console.error('[ProduccionDistribucion] Error eliminando prorrateo:', err);
      toast.error("Error al eliminar");
    }
  };

  const extraerGastos = (obs) => {
    if (!obs) return [];
    const matches = obs.match(/\[Prorrateo:.*?\]/g) || [];
    return matches.map(m => m.replace('[Prorrateo: ', '').replace(']', ''));
  };

  return (
    // <div className="w-full p-2 bg-[#F8FAFC] min-h-screen font-sans text-slate-700"> -> REEMPLAZADO POR CONTENEDOR NOVA
    <div className="imp-page imp-container animate-nova-in">
      
      {/* HEADER TIPO SISTEMA */}
      <div className="imp-header mb-4">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-10 h-10 bg-[#0B2A36] rounded-2xl flex items-center justify-center shadow-md shrink-0">
            <PieChart size={18} className="text-[#3FF2AA]" />
          </div>
          <div className="min-w-0">
            <h1 className="text-xl md:text-2xl font-black text-slate-800 tracking-tight uppercase truncate">Distribución de Gastos</h1>
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
              Ingeniería de costos indirectos
            </p>
          </div>
        </div>

        <div className="imp-header-actions">
           <button 
             type="button"
             onClick={onBack} 
             className="px-5 py-2.5 rounded-2xl bg-white border border-slate-200 text-slate-400 hover:text-[#51A7FD] shadow-sm hover:shadow-md transition-all text-[10px] font-black uppercase tracking-widest flex items-center gap-2"
           >
             <ArrowLeft size={16} /> Volver
           </button>
           <button 
             onClick={() => { setView(view === 'distribuir' ? 'historial' : 'distribuir'); setProducciones([]); }}
             className={`px-5 py-2.5 rounded-2xl shadow-sm hover:shadow-md transition-all text-[10px] font-black uppercase tracking-widest flex items-center gap-2
               ${view === 'distribuir' 
                  ? 'bg-white border border-slate-200 text-slate-500 hover:text-[#51A7FD]' 
                  : 'bg-[#0B2A36] text-white border border-[#0B2A36]'}`}
           >
             {view === 'distribuir' ? <><History size={16}/> Ver Historial</> : <><Calculator size={16}/> Nueva Distribución</>}
           </button>
        </div>
      </div>

      {/* KPI SECTION (REEMPLAZA GRID ANTERIOR) */}
      <div className="imp-kpis mb-3">
        <div className="imp-kpi">
          <div className="flex items-center justify-between">
            <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Inversión Lotes</p>
            <DollarSign size={16} className="text-emerald-500" />
          </div>
          <p className="mt-1.5 text-2xl font-black text-slate-800 tracking-tight">
            $ {inversionTotalProduccion.toLocaleString('es-AR', { minimumFractionDigits: 0 })}
          </p>
          {/* FIX UX #32: desglose claro entre costo bruto y prorrateos */}
          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
            Bruto: $ {inversionDirecta.toLocaleString('es-AR', { minimumFractionDigits: 0 })}
            {totalProrrateosAplicados > 0 && (
              <> · Prorrateos: $ {totalProrrateosAplicados.toLocaleString('es-AR', { minimumFractionDigits: 0 })}</>
            )}
          </p>
        </div>

        <div className="imp-kpi">
          <div className="flex items-center justify-between">
            <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Unidades Totales</p>
            <Layers size={16} className="text-slate-300" />
          </div>
          <p className="mt-1.5 text-2xl font-black text-slate-800 tracking-tight">
             {producciones.reduce((acc, p) => acc + Number(p.cantidad), 0)}
          </p>
          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">En periodo seleccionado</p>
        </div>

        <div className="imp-kpi">
            <div className="flex items-center justify-between">
                <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Registros</p>
                <FileText size={16} className="text-slate-300" />
            </div>
            <p className="mt-1.5 text-2xl font-black text-slate-800 tracking-tight">
                {producciones.length}
            </p>
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Órdenes encontradas</p>
        </div>
      </div>

      {/* BARRA DE FILTROS */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-3 mb-3">
         <div className="flex flex-col md:flex-row items-end gap-3">
            <div className="flex-1 w-full space-y-1">
               <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest ml-1">Fecha Desde</label>
               <input type="date" className="w-full bg-slate-50 border border-slate-100 rounded-xl py-2 px-3 text-[10px] font-bold outline-none focus:border-[#51A7FD] uppercase" value={fechaDesde} onChange={e => setFechaDesde(e.target.value)} />
            </div>
            <div className="flex-1 w-full space-y-1">
               <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest ml-1">Fecha Hasta</label>
               <input type="date" className="w-full bg-slate-50 border border-slate-100 rounded-xl py-2 px-3 text-[10px] font-bold outline-none focus:border-[#51A7FD] uppercase" value={fechaHasta} onChange={e => setFechaHasta(e.target.value)} />
            </div>
            <button 
                onClick={buscarProducciones} 
                disabled={loading} 
                className="w-full md:w-auto px-6 py-2.5 bg-[#51A7FD] text-white rounded-xl text-[10px] font-black uppercase tracking-widest flex items-center justify-center gap-2 hover:brightness-110 shadow-md shadow-blue-200 transition-all h-[38px]"
            >
              {loading ? <RefreshCcw size={14} className="animate-spin" /> : <Search size={14} />} Sincronizar
            </button>
         </div>
      </div>

      {/* TABLA DE RESULTADOS - ESTILO NOVA */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden mb-24 flex flex-col h-full">
        {/* Header tabla */}
        <div className="bg-[#0B2A36] px-5 py-3 border-b border-white/10 flex items-center justify-between">
             <div className="flex items-center gap-2">
                 <Calculator size={16} className="text-[#3FF2AA]" />
                 <span className="text-[10px] font-black text-white uppercase tracking-widest">
                     Listado de Órdenes
                 </span>
             </div>
             {view === 'distribuir' && (
                 <span className="text-[9px] font-bold text-white/50 uppercase tracking-widest">
                     {seleccionados.length} Seleccionados
                 </span>
             )}
        </div>

        <div className="overflow-x-auto imp-table-wrap">
          <table className="w-full text-left">
            <thead>
              <tr className="bg-slate-50/60 border-b border-slate-100 text-[9px] font-black text-slate-400 uppercase tracking-widest">
                {view === 'distribuir' && (
                  <th className="py-3 px-4 w-10 text-center">
                    <button onClick={() => { if (seleccionados.length === producciones.length) setSeleccionados([]); else setSeleccionados(producciones.map(p => p.id)); }}>
                      {seleccionados.length === producciones.length && producciones.length > 0 ? <CheckSquare size={16} className="text-[#51A7FD]"/> : <Square size={16}/>}
                    </button>
                  </th>
                )}
                <th className="py-3 px-4">Fecha (Edición Rápida)</th>
                <th className="py-3 px-4">Producto Terminado</th>
                <th className="py-3 px-4 text-center">Cant.</th>
                <th className="py-3 px-6 text-right">Costo Unit.</th>
                {view === 'historial' && <th className="py-3 px-6 text-right">Detalle</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {producciones.length === 0 ? (
                  <tr>
                      <td colSpan={6} className="py-12 text-center text-slate-300">
                          <p className="text-[9px] font-black uppercase tracking-[0.2em]">Sin registros cargados</p>
                      </td>
                  </tr>
              ) : (
                  producciones.map((p) => (
                    <React.Fragment key={p.id}>
                        <tr className={`group transition-colors cursor-pointer ${seleccionados.includes(p.id) ? 'bg-blue-50/30' : 'hover:bg-slate-50'}`}>
                        {view === 'distribuir' && (
                            <td className="py-3 px-4 text-center" onClick={() => toggleSeleccion(p.id)}>
                            {seleccionados.includes(p.id) ? <CheckSquare size={16} className="text-[#51A7FD] mx-auto"/> : <Square size={16} className="text-slate-200 mx-auto"/>}
                            </td>
                        )}
                        {/* CELDA DE FECHA - CLIC EN TODO EL CUADRO E ICONO NEGRO */}
                        <td className="p-0 relative group/date h-full min-w-[140px]">
                            <div className="flex items-center gap-2 px-4 py-3 h-full pointer-events-none">
                            <Calendar size={14} className="text-slate-900 group-hover/date:text-[#51A7FD] transition-colors" />
                            <span className="text-[10px] font-black text-slate-600 group-hover/date:text-[#51A7FD] uppercase tracking-wide">{formatearFechaLocal(p.fecha)}</span>
                            </div>
                            <input 
                            type="date" 
                            defaultValue={p.fecha ? p.fecha.split('T')[0] : ''} 
                            onChange={(e) => handleUpdateFecha(p.id, e.target.value)}
                            className="absolute inset-0 opacity-0 cursor-pointer w-full h-full z-10"
                            onClick={(e) => {
                                e.stopPropagation();
                                try { e.target.showPicker(); } catch(err) { /* showPicker no soportado en todos los browsers */ }
                            }}
                            />
                        </td>
                        <td className="py-3 px-4 text-[10px] font-black text-slate-800 uppercase tracking-tight">{p.producto_nombre}</td>
                        <td className="py-3 px-4 text-center text-[10px] font-bold text-slate-600">
                            <span className="bg-slate-100 text-slate-600 px-2 py-1 rounded-lg border border-slate-200">{p.cantidad}</span>
                        </td>
                        <td className="py-3 px-6 text-right text-[10px] font-black text-slate-700 tracking-tight">$ {(p.costo_unitario || 0).toLocaleString('es-AR', { minimumFractionDigits: 2 })}</td>
                        {view === 'historial' && (
                            <td className="py-3 px-6 text-right">
                            <button onClick={() => setExpandedId(expandedId === p.id ? null : p.id)} className="text-slate-300 hover:text-[#51A7FD] p-1.5 rounded-lg hover:bg-slate-100 transition-all">
                                {expandedId === p.id ? <ChevronUp size={16}/> : <ChevronDown size={16}/>}
                            </button>
                            </td>
                        )}
                        </tr>

                        {/* HISTORIAL EXPANDIBLE */}
                        {view === 'historial' && expandedId === p.id && (
                        <tr className="bg-slate-50/50 animate-in fade-in slide-in-from-top-1">
                            <td colSpan="6" className="p-4 px-8">
                            <div className="bg-white border border-slate-100 rounded-2xl p-4 shadow-inner space-y-3">
                                <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-2 flex items-center gap-2">
                                <FileText size={12}/> Prorrateos Aplicados a la Orden
                                </p>
                                {extraerGastos(p.observaciones).length === 0 ? (
                                    <p className="text-[9px] text-slate-300 italic">Sin costos adicionales distribuidos.</p>
                                ) : (
                                    extraerGastos(p.observaciones).map((g, i) => (
                                    <div key={i} className="flex justify-between items-center bg-slate-50 px-4 py-2.5 rounded-xl border border-slate-100">
                                        <span className="text-[10px] font-bold text-slate-600 uppercase tracking-wide">{g.split('|')[0].trim()}</span>
                                        <div className="flex items-center gap-4">
                                        <span className="text-[10px] font-black text-[#51A7FD]">Impacto: {g.split('|')[1]?.trim() || "$0.00"}</span>
                                        <button onClick={(e) => { e.stopPropagation(); handleRemoveDistribution(p, g); }} className="text-slate-300 hover:text-[#F05A57] p-1">
                                            <Trash2 size={14} />
                                        </button>
                                        </div>
                                    </div>
                                    ))
                                )}
                            </div>
                            </td>
                        </tr>
                        )}
                    </React.Fragment>
                  ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* BARRA DE DISTRIBUCIÓN REDUCIDA (Fixed Bottom) */}
      {view === 'distribuir' && producciones.length > 0 && (
        <div className="fixed bottom-6 right-6 w-fit max-w-4xl bg-white p-4 rounded-3xl shadow-2xl border border-slate-100 flex items-center gap-4 z-50 animate-in slide-in-from-bottom-4 ring-1 ring-slate-900/5">
          <div className="flex flex-col min-w-[120px] border-r border-slate-100 pr-4">
            <span className="text-[9px] font-black text-[#51A7FD] uppercase tracking-widest leading-none mb-1">Órdenes: {seleccionados.length}</span>
            <span className="text-[9px] font-bold text-slate-400 uppercase tracking-tight">Total Unid: {totalUnidadesSeleccionadas}</span>
          </div>
          
          <div className="flex items-center gap-2">
            <div className="relative">
                <input 
                type="text" placeholder="CONCEPTO (EJ: LUZ)" 
                className="w-40 bg-slate-50 border border-slate-200 rounded-xl py-2 px-3 text-[10px] font-bold text-slate-700 outline-none uppercase focus:border-[#51A7FD] focus:bg-white transition-all" 
                value={concepto} onChange={e => setConcepto(e.target.value.toUpperCase())} 
                />
            </div>
            
            <div className="relative w-28">
              <span className="absolute left-3 top-2 text-slate-400 font-bold text-[10px]">$</span>
              <input 
                type="number" placeholder="MONTO" 
                className="w-full bg-slate-50 border border-slate-200 rounded-xl py-2 pl-6 pr-3 text-[10px] font-black text-slate-800 outline-none focus:border-[#51A7FD] focus:bg-white transition-all" 
                value={montoTotal} onChange={e => setMontoTotal(e.target.value)} 
              />
            </div>
            
            <button 
              onClick={handleAplicarCostos} 
              disabled={saving || isSuccess || seleccionados.length === 0} 
              className={`px-6 py-2 rounded-xl font-black text-[10px] uppercase tracking-widest transition-all flex items-center gap-2 shadow-lg shadow-emerald-500/20
                ${isSuccess 
                    ? 'bg-[#3ff2aa] text-[#0B2A36]' 
                    : 'bg-[#3ff2aa] text-[#0B2A36] hover:scale-[1.02] active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed'}`}
            >
              {isSuccess ? <><Check size={14} strokeWidth={4} /> Aplicado</> : saving ? <><RefreshCcw size={14} className="animate-spin" /> ...</> : <><Calculator size={14} /> Aplicar</>}
            </button>
          </div>
        </div>
      )}

      <style>{`
        .animate-nova-in { animation: novaFadeIn 0.28s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
        @keyframes novaFadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
        .imp-table-wrap { width: 100%; overflow-x: auto; }
      `}</style>
    </div>
  );
}

export default ProduccionDistribucion;
